// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { UseMutationResult, useMutation } from '@tanstack/react-query';
import {
  CommercialAgreement,
  CommercialAgreement_LockCoin_Result,
} from '@daml.js/utility-commercials-v0/lib/Utility/Commercials/V0/Model/CommercialAgreement';
import { ContractId } from '@daml/types';
import { useLedgerApiClient } from '../../../contexts/LedgerApiContext';
import { useScanProxyApiClient } from '../../../contexts/ScanProxyApiContext';
import { appTransferContextWithDisclosures } from '../../../utils/appTransferContextWithDisclosures';
import useCoins from '../../queries/cn/useCoin';

export const useCommercialAgreementLockCoin: () => UseMutationResult<
  CommercialAgreement_LockCoin_Result,
  string,
  {
    commercialAgreementCid: ContractId<CommercialAgreement>;
    targetAmount: string;
  }
> = () => {
  const ledgerApi = useLedgerApiClient();
  const scanProxyApi = useScanProxyApiClient();
  const amulets = useCoins();
  const amuletCids = (amulets.data ?? []).map((a) => a.contractId);

  return useMutation({
    mutationKey: ['commercialAgreementLockCoin', ledgerApi],
    mutationFn: async ({ commercialAgreementCid, targetAmount }) => {
      const { appTransferContext, disclosedContracts } = await appTransferContextWithDisclosures(
        scanProxyApi,
        30,
      );

      return ledgerApi.commercialAgreementLockCoin(
        commercialAgreementCid,
        targetAmount,
        amuletCids,
        appTransferContext,
        disclosedContracts,
      );
    },
  });
};
